import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import {
  APP_LOGOS,
  isAppLogoSlug,
  type AppLogoSlug,
} from '../ui/icons/appLogos';
import type { AppTypeDetail } from '../shared/appTypes';
import { appLogoSlug, LOGO_ANNOTATION } from '../shared/appIcons';
import {
  declaredTemplates,
  GENERIC_TEMPLATES,
  ODOO_CREATE_TEMPLATE,
} from '../shared/templateRoles';

/**
 * Who looks at the list: the admin tier, or anyone else. Decides the tag of
 * the project template and the « Bientôt » card — presentation only, the
 * catalog already lists only the templates the caller may read.
 */
export type Audience = 'admin' | 'member';

/**
 * The logo on a tile (`TemplateLogo.tsx`): an application's
 * (components/ui/icons/appLogos.ts), the danger bin of a deletion, or the generic
 * template glyph on a neutral tile.
 */
export type CardLogo = AppLogoSlug | 'delete' | 'template';

/**
 * The categories of « Créer », in the order of the filter chips and of the
 * tiles. « Administration »: the section of the admin tier's templates,
 * apart below the others (`isAdminCard`).
 */
export const CATEGORIES = [
  'Gestion',
  'Collaboration',
  'Formation',
  'Communication',
  'Automatisation',
  'Sécurité',
  'Site web',
  'Autres',
  'Administration',
] as const;

export type TemplateCategory = (typeof CATEGORIES)[number];

/** A template's category without one of its own. */
export const DEFAULT_CATEGORY: TemplateCategory = 'Autres';

/**
 * The annotations a template of the catalog may carry to place itself
 * (catalog repository, documented in CUSTOMIZATIONS.md): one of `CATEGORIES`
 * but « Administration », which only the admin-only label gives, and a slug
 * of components/ui/icons/appLogos.ts. An unknown value counts as none.
 */
export const CATEGORY_ANNOTATION = 'waadoo.ovh/category';
export { LOGO_ANNOTATION };

export interface CardTag {
  label: string;
  /** `admin`: the red pill with a lock; `neutral`: the grey one. */
  kind: 'admin' | 'neutral';
}

/** One card of the « Créer » list (`TemplateCards.tsx`). */
export interface TemplateCard {
  /** `template:<namespace>/<name>`, lower case (`stringifyEntityRef`). */
  ref: string;
  namespace: string;
  name: string;
  title: string;
  description: string;
  category: TemplateCategory;
  logo: CardLogo;
  tag?: CardTag;
  action: 'Commencer' | 'Ouvrir';
}

/**
 * The label that reserves an entity to the admin tier — the backend's
 * `ADMIN_ONLY_LABEL` (permissionsPolicyExtension.ts), which excludes it from a
 * non-admin's catalog reads whatever its value. Hence the « admin » tag on
 * any template carrying it (`appenv`, `project-delete`).
 */
export const ADMIN_ONLY_LABEL = 'waadoo.ovh/admin-only';

/**
 * The label of a template launched only from a button of the app — the wake
 * and the sleep of an environment (`env-wake`, `env-sleep`), whose
 * project and environment the button passes. Left out of the « Créer » list
 * whatever its value; PRESENTATION ONLY: the catalog still serves it to
 * whoever may read it, and the scaffolder runs it — its actions decide who
 * may (permission policy and the actions' own guards, backend).
 */
export const HIDDEN_LABEL = 'waadoo.ovh/hidden';

export const ADMIN_TAG: CardTag = { label: 'admin', kind: 'admin' };

interface Presentation {
  category: TemplateCategory;
  logo: CardLogo;
  title: string;
  description: string;
  tag: (audience: Audience) => CardTag;
  action: TemplateCard['action'];
}

/**
 * The templates the app knows, in the order they are listed within their
 * category: their own title, description, category, logo and tag (mockups
 * « U2 » and « A », 2026-10-15; « D1 », 2026-10-19). Any other template
 * keeps the catalog's title and description, and its annotations' category
 * and logo — but an AppType's creation template, titled after its type
 * (`appTypeCreation`).
 */
const PRESENTATIONS = new Map<string, Presentation>([
  [
    ODOO_CREATE_TEMPLATE,
    {
      category: 'Gestion',
      logo: 'odoo',
      title: 'Projet Odoo',
      description:
        'Dépôt GitLab, environnement dev et branches éphémères de test pour un client.',
      tag: audience =>
        audience === 'admin'
          ? { label: 'tous', kind: 'neutral' }
          : { label: '≈ 1 min', kind: 'neutral' },
      action: 'Commencer',
    },
  ],
  [
    // The generic deletion of step 7, which every type may designate.
    GENERIC_TEMPLATES.delete,
    {
      category: 'Administration',
      logo: 'delete',
      title: 'Supprimer un projet',
      description:
        'Simulation d’abord, puis suppression réelle : environnements, buckets, groupes, catalogue.',
      tag: () => ADMIN_TAG,
      action: 'Ouvrir',
    },
  ],
]);

const KNOWN_ORDER = Array.from(PRESENTATIONS.keys());

function isAdminOnly(entity: Entity): boolean {
  return entity.metadata.labels?.[ADMIN_ONLY_LABEL] !== undefined;
}

function annotatedCategory(entity: Entity): TemplateCategory {
  const value = entity.metadata.annotations?.[CATEGORY_ANNOTATION];
  const category = CATEGORIES.find(
    known => known === value && known !== 'Administration',
  );
  return category ?? DEFAULT_CATEGORY;
}

function annotatedLogo(entity: Entity): CardLogo {
  const value = entity.metadata.annotations?.[LOGO_ANNOTATION];
  return value !== undefined && isAppLogoSlug(value) ? value : 'template';
}

/**
 * The AppType whose creation template `ref` is (`spec.templates.create`),
 * among `appTypes`; the first by title when several designate it.
 */
function appTypeCreation(
  ref: string,
  appTypes: readonly AppTypeDetail[],
): AppTypeDetail | undefined {
  return appTypes.find(appType => declaredTemplates(appType).create === ref);
}

/**
 * A template's card: its known presentation, else — for the creation
 * template of an AppType — « Projet <type> » with the type's description
 * and logo (`appLogoSlug`) when the template's annotations give none, else the catalog's
 * words and annotations. The admin-only label puts any template in
 * « Administration ».
 */
export function templateCard(
  entity: Entity,
  audience: Audience,
  appTypes: readonly AppTypeDetail[] = [],
): TemplateCard {
  const ref = stringifyEntityRef(entity);
  const namespace = entity.metadata.namespace ?? 'default';
  const name = entity.metadata.name;
  const known = PRESENTATIONS.get(ref);
  if (known) {
    return {
      ref,
      namespace,
      name,
      title: known.title,
      description: known.description,
      category: isAdminOnly(entity) ? 'Administration' : known.category,
      logo: known.logo,
      tag: isAdminOnly(entity) ? ADMIN_TAG : known.tag(audience),
      action: known.action,
    };
  }
  const creation = appTypeCreation(ref, appTypes);
  // The type's logo as everywhere else (`appLogoSlug`): its annotation, else
  // its name when it is a slug.
  const typeLogo = creation
    ? appLogoSlug(creation.name, creation.annotations)
    : undefined;
  const logo = annotatedLogo(entity);
  return {
    ref,
    namespace,
    name,
    title: creation
      ? `Projet ${creation.title}`
      : entity.metadata.title ?? name,
    description: entity.metadata.description ?? creation?.description ?? '',
    category: isAdminOnly(entity)
      ? 'Administration'
      : annotatedCategory(entity),
    logo: logo === 'template' && typeLogo !== undefined ? typeLogo : logo,
    ...(isAdminOnly(entity) ? { tag: ADMIN_TAG } : {}),
    action: 'Commencer',
  };
}

/** Whether a template carries `HIDDEN_LABEL`. */
export function isHiddenTemplate(entity: Entity): boolean {
  return entity.metadata.labels?.[HIDDEN_LABEL] !== undefined;
}

/**
 * The cards of the templates the catalog returned, those carrying
 * `HIDDEN_LABEL` left out, by category (`CATEGORIES`); within
 * one, the known ones first, in their order, then the others by title.
 * `appTypes`: the descriptors read, none while they cannot be.
 */
export function templateCards(
  entities: Entity[],
  audience: Audience,
  appTypes: readonly AppTypeDetail[] = [],
): TemplateCard[] {
  const shown = entities.filter(entity => !isHiddenTemplate(entity));
  const rank = (card: TemplateCard) => {
    const index = KNOWN_ORDER.indexOf(card.ref);
    return index === -1 ? KNOWN_ORDER.length : index;
  };
  const categoryRank = (card: TemplateCard) =>
    CATEGORIES.indexOf(card.category);
  return shown
    .map(entity => templateCard(entity, audience, appTypes))
    .sort(
      (a, b) =>
        categoryRank(a) - categoryRank(b) ||
        rank(a) - rank(b) ||
        a.title.localeCompare(b.title, 'fr'),
    );
}

/** Whether a card goes in the « Administration » section, apart. */
export function isAdminCard(card: TemplateCard): boolean {
  return card.category === 'Administration';
}

/** The categories the cards have, in `CATEGORIES`' order. */
export function presentCategories(cards: TemplateCard[]): TemplateCategory[] {
  return CATEGORIES.filter(category =>
    cards.some(card => card.category === category),
  );
}

/** From this many templates on, the list gets its toolbar. */
export const TOOLBAR_FROM_TEMPLATES = 6;

/** The chips need this many categories, « Administration » not counted. */
export const CHIPS_FROM_CATEGORIES = 2;

/**
 * The toolbar above the tiles, `undefined` for none: only from
 * `TOOLBAR_FROM_TEMPLATES` templates on — a short list reads at a glance,
 * and its « Administration » chip would only repeat the section. Then the
 * search, and the category chips (after « Tous ») when the cards have
 * `CHIPS_FROM_CATEGORIES` categories besides « Administration »; its chip
 * comes last when there is an admin template.
 */
export function templateToolbar(
  cards: TemplateCard[],
): { chips: TemplateCategory[] } | undefined {
  if (cards.length < TOOLBAR_FROM_TEMPLATES) return undefined;
  const categories = presentCategories(cards);
  const sorting = categories.filter(category => category !== 'Administration');
  return {
    chips: sorting.length >= CHIPS_FROM_CATEGORIES ? categories : [],
  };
}

/** Lower case, accents dropped: « Sécurité » reads as « securite ». */
export function foldText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr');
}

/**
 * Whether a card answers a search: every word of the query in its title,
 * description, category, template name (`metadata.name`) or the name of
 * its logo's application (« Nextcloud »), whatever the case and the
 * accents. An empty query matches every card.
 */
export function matchesQuery(card: TemplateCard, query: string): boolean {
  const application = isAppLogoSlug(card.logo)
    ? APP_LOGOS[card.logo].title
    : '';
  const haystack = foldText(
    `${card.title} ${card.description} ${card.category} ${card.name} ${application}`,
  );
  return foldText(query)
    .split(/\s+/)
    .filter(word => word !== '')
    .every(word => haystack.includes(word));
}

/** Below this many templates, a non-admin also gets the « Bientôt » card. */
export const SOON_CARD_BELOW = 3;

/**
 * The dashed « Bientôt » card: for a non-admin who sees at least one
 * template and fewer than `SOON_CARD_BELOW`. Never for an admin; not on an
 * empty list either, which says so itself.
 */
export function showSoonCard(count: number, audience: Audience): boolean {
  return audience === 'member' && count > 0 && count < SOON_CARD_BELOW;
}
